import React, { memo, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { CompanyEventProto } from '../../proto/generated/company_event'
import { Box, Popover, useTheme } from '@mui/material'
import { companyEventsFetcher, corporateActionsFetcher } from '../../selectors/companyEvents'
import useStyles from './styles'
import { CorporateAction } from '../../proto/generated/corporate-action'
import EventDetailsList from './eventDetailsList'

interface CompanyEventsProps {
    companyCode: string
    onPopoverOpenChange?: (isOpen: boolean) => void
}

const eventMapping: Record<string, { letter: string; color: string }> = {
    dividend: { letter: 'D', color: '#0f89e5' },
    split: { letter: 'S', color: 'rgb(251, 140, 0)' },
    bonus: { letter: 'B', color: '#B8860B' },
    rights: { letter: 'R', color: 'rgb(171, 71, 188)' },
    default: { letter: 'E', color: 'skyblue' }
}

const getCorporateActionKey = (type: string): string => {
    const normalizedType = type.toLowerCase()

    if (normalizedType === 'final_dividend' || normalizedType === 'special_dividend' || normalizedType === 'interim_dividend') {
        return 'dividend'
    } else if (normalizedType === 'split') {
        return 'split'
    } else if (normalizedType === 'bonus') {
        return 'bonus'
    } else if (normalizedType === 'rights') {
        return 'rights'
    }

    return 'default'
}

const CompanyEvents: React.FC<CompanyEventsProps> = ({ companyCode, onPopoverOpenChange }) => {
    const { classes } = useStyles()
    const [displayedEventTypes, setDisplayedEventTypes] = useState<string[]>([])
    const [selectedEventType, setSelectedEventType] = useState<string | null>(null)
    const companyEvents = useSelector(companyEventsFetcher)
    const corporateActions = useSelector(corporateActionsFetcher)
    const theme = useTheme()
    const today = new Date().toISOString().split('T')[0]

    const filteredEvents = useMemo(() => {
        return (companyEvents[companyCode] || []).filter((event: CompanyEventProto) => event.date === today)
    }, [companyEvents, companyCode, today])

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

    const handleIconClick = (event: React.MouseEvent<HTMLElement>, typeClicked: string | null) => {
        event.stopPropagation()
        const isSame = selectedEventType === typeClicked
        setSelectedEventType(isSame ? null : typeClicked)
        setAnchorEl(isSame ? null : event.currentTarget)
        onPopoverOpenChange?.(!isSame)
    }

    const handlePopoverClose = (e) => {
        e.stopPropagation()
        setAnchorEl(null)
        setSelectedEventType(null)
        onPopoverOpenChange?.(false)
    }

    const isPopoverOpen = Boolean(anchorEl)

    useEffect(() => {
        const today = new Date().toISOString().split('T')[0]
        const eventTypesSet = new Set<string>()
        const todayCompanyEvents = (companyEvents[companyCode] || []).filter((event: CompanyEventProto) => event.date === today)
        if (todayCompanyEvents.length) {
            eventTypesSet.add('default')
        }
        const startOfToday = new Date()
        startOfToday.setHours(0, 0, 0, 0)
        const startOfTodayMs = startOfToday.getTime()
        const startOfTomorrow = new Date(startOfToday)
        startOfTomorrow.setDate(startOfTomorrow.getDate() + 1)
        const startOfTomorrowMs = startOfTomorrow.getTime()
        const todayCorporateActions = (corporateActions[companyCode] || []).filter((action: CorporateAction) => {
            if (!action.exDate) return false
            return action.exDate >= startOfTodayMs && action.exDate < startOfTomorrowMs
        })

        todayCorporateActions.forEach((action: CorporateAction) => {
            if (action.type) {
                const key = getCorporateActionKey(action.type.toString())
                eventTypesSet.add(key)
            }
        })

        setDisplayedEventTypes([...eventTypesSet])
    }, [companyEvents, corporateActions, companyCode])

    return (
        <>
            {displayedEventTypes.length > 0 && (
                <Box className={classes.eventIconContainer} sx={{ position: 'relative', width: 24, height: 24 }}>
                    {displayedEventTypes.slice(0, 2).map((eventTypeKey, index) => {
                        const { letter, color } = eventMapping[eventTypeKey] || eventMapping['default']
                        const isSelected = selectedEventType === eventTypeKey

                        return (
                            <Box
                                key={eventTypeKey}
                                onClick={(e) => handleIconClick(e, eventTypeKey)}
                                onMouseDown={(e) => e.stopPropagation()}
                                sx={{
                                    position: 'absolute',
                                    left: `${index * 11}px`,
                                    width: 20,
                                    height: 20,
                                    borderRadius: '50%',
                                    backgroundColor: theme.palette.background.paper,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    boxShadow: isSelected ? `0 0 0 2px ${color}` : undefined
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: '50%',
                                        backgroundColor: isSelected ? color : theme.palette.background.paper,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: `1.5px solid ${color}`,
                                        transition: 'all 0.1s ease-in-out'
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: '10px',
                                            fontWeight: 500,
                                            color: isSelected ? '#fff' : color,
                                            lineHeight: 1
                                        }}
                                    >
                                        {letter}
                                    </span>
                                </Box>
                            </Box>
                        )
                    })}
                </Box>
            )}
            <Popover
                open={isPopoverOpen}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{
                    vertical: anchorEl && anchorEl.getBoundingClientRect().top < 200 ? 'bottom' : 'top',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: anchorEl && anchorEl.getBoundingClientRect().top < 200 ? 'top' : 'bottom',
                    horizontal: 'center',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            maxWidth: 400,
                            marginTop: anchorEl && anchorEl.getBoundingClientRect().top < 200 ? '8px' : '-8px',
                        }
                    }
                }}
            >
                <EventDetailsList
                    eventDetails={filteredEvents}
                    corporateActions={corporateActions[companyCode] || []}
                />
            </Popover>
        </>
    );
};

export default memo(CompanyEvents);